/**
 * 标准配合求解几何读取与矩阵工具。
 *
 * 文件职责：
 * 1. 从可序列化 MateEntityRef 读取求解需要的平面、轴线、点与球；
 * 2. 提供组件 transform 与世界空间 delta 的基础矩阵工具；
 * 3. 不依赖 Viewer、Object3D 或业务文档状态。
 */

import * as THREE from 'three';
import type { ComponentTransformSnapshot } from '../../component-instance';
import {
  MateEntityKind,
  type MateEntityRef,
} from '../../definition';

/**
 * 轴线几何。
 */
export interface AxisGeometry {
  /** 轴上一点。 */
  readonly origin: THREE.Vector3;
  /** 轴方向。 */
  readonly direction: THREE.Vector3;
  /** 半径。 */
  readonly radius?: number;
}

/**
 * 圆锥几何。
 */
export interface ConeGeometry extends AxisGeometry {
  /** 圆锥顶点。 */
  readonly apex: THREE.Vector3;
  /** 圆锥半角。 */
  readonly halfAngle: number;
}

/**
 * 平面几何。
 */
export interface PlaneGeometry {
  /** 平面上一点。 */
  readonly point: THREE.Vector3;
  /** 平面法向。 */
  readonly normal: THREE.Vector3;
}

/**
 * 球几何。
 */
export interface SphereGeometry {
  /** 球心。 */
  readonly center: THREE.Vector3;
  /** 半径。 */
  readonly radius: number;
}

/**
 * 点几何。
 */
export interface PointGeometry {
  /** 点坐标。 */
  readonly point: THREE.Vector3;
}

/**
 * 将三元组转换为 Vector3。
 *
 * @param value 三元组。
 * @returns Vector3。
 */
export function vectorFromTuple(value: TopologyPoint3D): THREE.Vector3 {
  return new THREE.Vector3(value[0], value[1], value[2]);
}

/**
 * 将 Vector3 转换为三元组。
 *
 * @param value Vector3。
 * @returns 三元组。
 */
export function tupleFromVector(value: THREE.Vector3): readonly [number, number, number] {
  return [value.x, value.y, value.z] as const;
}

/**
 * 归一化向量。
 *
 * @param value 源向量。
 * @param epsilon 容差。
 * @returns 单位向量；退化时返回 null。
 */
export function normalizeVector(value: THREE.Vector3, epsilon: number): THREE.Vector3 | null {
  const length = value.length();
  if (!Number.isFinite(length) || length <= epsilon) {
    return null;
  }
  return value.clone().multiplyScalar(1 / length);
}

/**
 * 从 transform 组合矩阵。
 *
 * @param transform transform 快照。
 * @returns 变换矩阵。
 */
export function composeMatrix(transform: ComponentTransformSnapshot): THREE.Matrix4 {
  return new THREE.Matrix4().compose(
    vectorFromTuple(transform.position as TopologyPoint3D),
    new THREE.Quaternion(
      transform.quaternion[0],
      transform.quaternion[1],
      transform.quaternion[2],
      transform.quaternion[3],
    ),
    vectorFromTuple(transform.scale as TopologyPoint3D),
  );
}

/**
 * 从矩阵还原 transform。
 *
 * @param matrix 变换矩阵。
 * @returns transform 快照。
 */
export function decomposeMatrix(matrix: THREE.Matrix4): ComponentTransformSnapshot {
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  matrix.decompose(position, quaternion, scale);

  return {
    position: tupleFromVector(position),
    quaternion: [quaternion.x, quaternion.y, quaternion.z, quaternion.w] as const,
    scale: tupleFromVector(scale),
  };
}

/**
 * 应用世界空间刚体 delta 到组件 transform。
 *
 * 设计说明（通用刚体收口，A 方案）：
 * 配合求解对零件只做刚体（旋转+平移）运动，零件的缩放与“四元数是单位长度”都是刚体运动下的不变量、
 * 绝不能被求解改变。旧实现走 `compose→矩阵乘→decompose` 往返，会把四元数的非单位长度 |q|² 烤进矩阵旋转块、
 * 再被 decompose 当成缩放读回并存下，逐帧形成正反馈把缩放（甚至各轴不一致地）放大，最终把几何等比拉偏、
 * 顶破已提交同轴心/重合残差。这里改为**直接在流形自身坐标上走一步**：把 delta 拆成刚体旋转/平移，直接作用到
 * `(position, quaternion)`，四元数每步重新归一化，`scale` 根本不进入计算——从源头消除往返，而非事后修补。
 *
 * @param transform 当前 transform。
 * @param delta 世界空间刚体 delta 矩阵。
 * @param worldTransform 当前 world transform；提供时先把 delta 转到父级 local 帧再作用。
 * @returns 更新后 transform（缩放严格保持不变、四元数保持单位长度）。
 */
export function applyWorldDeltaToTransform(
  transform: ComponentTransformSnapshot,
  delta: THREE.Matrix4,
  worldTransform?: ComponentTransformSnapshot,
): ComponentTransformSnapshot {
  const localDelta = worldTransform
    ? convertWorldDeltaToLocalFrameDelta(delta, worldTransform, transform)
    : delta;
  return applyRigidLocalDeltaToPose(transform, localDelta);
}

/**
 * 把世界空间刚体 delta 转换到组件父级 local 帧下的刚体 delta。
 *
 * 说明：
 * 嵌套（存在 world transform）时，世界 delta 需要先经 `parentWorld⁻¹ · delta · parentWorld` 共轭到 local 帧，
 * 才能作用到组件 local 位姿。此处只用矩阵完成“帧变换”，随后由 {@link applyRigidLocalDeltaToPose} 以直接流形
 * 步进作用到 `(position, quaternion)`，因此往返只发生在一次性的帧变换、不会逐帧累积到组件状态。
 *
 * @param delta 世界空间刚体 delta。
 * @param worldTransform 组件当前 world transform。
 * @param transform 组件当前 local transform。
 * @returns 父级 local 帧下的刚体 delta 矩阵。
 */
function convertWorldDeltaToLocalFrameDelta(
  delta: THREE.Matrix4,
  worldTransform: ComponentTransformSnapshot,
  transform: ComponentTransformSnapshot,
): THREE.Matrix4 {
  const localMatrix = composeMatrix(transform);
  const worldMatrix = composeMatrix(worldTransform);
  const parentWorldMatrix = worldMatrix.clone().multiply(localMatrix.clone().invert());
  return parentWorldMatrix.clone().invert().multiply(delta).multiply(parentWorldMatrix);
}

/**
 * 以直接流形步进，把 local 帧下的刚体 delta 作用到组件位姿。
 *
 * 说明：
 * 对刚体 delta = T(dp)·R(dq)、当前位姿 = T(p)·R(q)·S(s)，则 delta·当前 分解后严格满足
 * `p' = R(dq)·p + dp`、`q' = R(dq)·q`、`s' = s`。因此直接按该公式更新 `(position, quaternion)`、
 * 四元数归一化、缩放原样透传，等价于矩阵路径但**不产生 compose/decompose 往返**，杜绝缩放/四元数漂移。
 *
 * @param transform 当前 transform。
 * @param localDelta 组件父级 local 帧下的刚体 delta。
 * @returns 更新后 transform。
 */
function applyRigidLocalDeltaToPose(
  transform: ComponentTransformSnapshot,
  localDelta: THREE.Matrix4,
): ComponentTransformSnapshot {
  const deltaPosition = new THREE.Vector3();
  const deltaQuaternion = new THREE.Quaternion();
  const deltaScale = new THREE.Vector3();
  // delta 是刚体变换，deltaScale ≈ 1，仅取旋转与平移；缩放刻意不参与后续计算。
  localDelta.decompose(deltaPosition, deltaQuaternion, deltaScale);

  const currentPosition = vectorFromTuple(transform.position as TopologyPoint3D);
  const currentQuaternion = new THREE.Quaternion(
    transform.quaternion[0],
    transform.quaternion[1],
    transform.quaternion[2],
    transform.quaternion[3],
  );

  const nextPosition = currentPosition.applyQuaternion(deltaQuaternion).add(deltaPosition);
  const nextQuaternion = deltaQuaternion.multiply(currentQuaternion).normalize();

  return {
    position: [nextPosition.x, nextPosition.y, nextPosition.z] as const,
    quaternion: [nextQuaternion.x, nextQuaternion.y, nextQuaternion.z, nextQuaternion.w] as const,
    // 缩放是刚体运动不变量，原样透传，绝不由求解重算。
    scale: [...transform.scale] as readonly [number, number, number],
  };
}

/**
 * 创建平移 delta。
 *
 * @param delta 平移向量。
 * @returns 矩阵。
 */
export function createTranslationDelta(delta: THREE.Vector3): THREE.Matrix4 {
  return new THREE.Matrix4().makeTranslation(delta.x, delta.y, delta.z);
}

/**
 * 创建绕世界点旋转 delta。
 *
 * @param origin 旋转中心。
 * @param rotation 旋转四元数。
 * @returns 矩阵。
 */
export function createRotationDeltaAroundPoint(
  origin: THREE.Vector3,
  rotation: THREE.Quaternion,
): THREE.Matrix4 {
  const toOrigin = new THREE.Matrix4().makeTranslation(-origin.x, -origin.y, -origin.z);
  const rotationMatrix = new THREE.Matrix4().makeRotationFromQuaternion(rotation);
  const back = new THREE.Matrix4().makeTranslation(origin.x, origin.y, origin.z);
  return back.multiply(rotationMatrix).multiply(toOrigin);
}

/**
 * 根据两个方向创建旋转四元数。
 *
 * @param from 源方向。
 * @param to 目标方向。
 * @param epsilon 容差。
 * @returns 旋转四元数。
 */
export function createRotationBetweenDirections(
  from: THREE.Vector3,
  to: THREE.Vector3,
  epsilon: number,
): THREE.Quaternion | null {
  const fromNormalized = normalizeVector(from, epsilon);
  const toNormalized = normalizeVector(to, epsilon);
  if (!fromNormalized || !toNormalized) {
    return null;
  }
  return new THREE.Quaternion().setFromUnitVectors(fromNormalized, toNormalized);
}

/**
 * 根据两个方向创建绕世界点旋转的 delta 矩阵。
 *
 * 设计说明：
 * 距离、重合、方向类 Mate 都需要“先对齐方向再应用偏移”的同一基础能力。
 * 将该函数放在几何访问模块，可以避免各求解分支重复拼装旋转矩阵。
 *
 * @param origin 旋转中心。
 * @param from 源方向。
 * @param to 目标方向。
 * @param epsilon 容差。
 * @returns 旋转 delta 矩阵；方向退化时返回 null。
 */
export function createRotationDeltaBetweenDirections(
  origin: THREE.Vector3,
  from: THREE.Vector3,
  to: THREE.Vector3,
  epsilon: number,
): THREE.Matrix4 | null {
  const rotation = createRotationBetweenDirections(from, to, epsilon);
  if (!rotation) {
    return null;
  }
  return createRotationDeltaAroundPoint(origin, rotation);
}

/**
 * 为方向构造稳定的垂直方向。
 *
 * 设计说明：
 * 点到轴、轴到轴和方向 Mate 在几何退化时都需要一个可预测的兜底方向。
 * 这里固定使用与输入方向夹角更大的世界轴作为种子，保证同一输入得到稳定输出。
 *
 * @param direction 源方向。
 * @param epsilon 容差。
 * @returns 垂直方向。
 */
export function resolvePerpendicularDirection(direction: THREE.Vector3, epsilon: number): THREE.Vector3 {
  const seed = Math.abs(direction.x) < 0.9
    ? new THREE.Vector3(1, 0, 0)
    : new THREE.Vector3(0, 1, 0);
  const perpendicular = direction.clone().cross(seed);
  return normalizeVector(perpendicular, epsilon) ?? new THREE.Vector3(0, 0, 1);
}

/**
 * 根据 descriptor 读取平面。
 *
 * @param entity 实体引用。
 * @returns 平面几何。
 */
export function resolvePlaneGeometry(entity: MateEntityRef): PlaneGeometry | null {
  const geometry = entity.geometry as BRepGeometryDescriptor & Record<string, unknown>;
  if (entity.entityKind !== MateEntityKind.Plane && entity.entityKind !== MateEntityKind.ReferencePlane) {
    return null;
  }
  if (!Array.isArray(geometry.point) || !Array.isArray(geometry.normal)) {
    return null;
  }
  return {
    point: vectorFromTuple(geometry.point as TopologyPoint3D),
    normal: vectorFromTuple(geometry.normal as TopologyPoint3D).normalize(),
  };
}

/**
 * 根据 descriptor 读取轴线。
 *
 * @param entity 实体引用。
 * @returns 轴线几何。
 */
export function resolveAxisGeometry(entity: MateEntityRef): AxisGeometry | null {
  const geometry = entity.geometry as BRepGeometryDescriptor & Record<string, unknown>;
  if (entity.entityKind === MateEntityKind.Line || entity.entityKind === MateEntityKind.ReferenceAxis) {
    const origin = Array.isArray(geometry.start)
      ? vectorFromTuple(geometry.start as TopologyPoint3D)
      : (Array.isArray(geometry.origin) ? vectorFromTuple(geometry.origin as TopologyPoint3D) : null);
    const direction = Array.isArray(geometry.direction)
      ? vectorFromTuple(geometry.direction as TopologyPoint3D)
      : null;
    if (!origin || !direction) {
      return null;
    }
    return { origin, direction: direction.normalize() };
  }

  if (
    entity.entityKind === MateEntityKind.Cylinder
    || entity.entityKind === MateEntityKind.Cone
    || entity.entityKind === MateEntityKind.CircularEdge
  ) {
    const axisOrigin = Array.isArray(geometry.axisOrigin)
      ? vectorFromTuple(geometry.axisOrigin as TopologyPoint3D)
      : (Array.isArray(geometry.center) ? vectorFromTuple(geometry.center as TopologyPoint3D) : null);
    const axisDirection = Array.isArray(geometry.axisDirection)
      ? vectorFromTuple(geometry.axisDirection as TopologyPoint3D)
      : (Array.isArray(geometry.normal) ? vectorFromTuple(geometry.normal as TopologyPoint3D) : null);
    if (!axisOrigin || !axisDirection) {
      return null;
    }
    const radius = typeof geometry.radius === 'number' && Number.isFinite(geometry.radius)
      ? geometry.radius
      : undefined;
    return { origin: axisOrigin, direction: axisDirection.normalize(), radius };
  }

  return null;
}

/**
 * 根据 descriptor 读取圆锥几何。
 *
 * @param entity 实体引用。
 * @returns 圆锥几何；缺少顶点或半角时返回 null。
 */
export function resolveConeGeometry(entity: MateEntityRef): ConeGeometry | null {
  const geometry = entity.geometry as BRepGeometryDescriptor & Record<string, unknown>;
  if (entity.entityKind !== MateEntityKind.Cone) {
    return null;
  }

  const axis = resolveAxisGeometry(entity);
  if (!axis || !Array.isArray(geometry.apex)) {
    return null;
  }

  const halfAngle = geometry.halfAngle;
  if (
    typeof halfAngle !== 'number'
    || !Number.isFinite(halfAngle)
    || halfAngle <= 0
    || halfAngle >= Math.PI / 2
  ) {
    return null;
  }

  return {
    ...axis,
    apex: vectorFromTuple(geometry.apex as TopologyPoint3D),
    halfAngle,
  };
}

/**
 * 根据 descriptor 读取点。
 *
 * @param entity 实体引用。
 * @returns 点几何。
 */
export function resolvePointGeometry(entity: MateEntityRef): PointGeometry | null {
  const geometry = entity.geometry as BRepGeometryDescriptor & Record<string, unknown>;
  if (
    entity.entityKind !== MateEntityKind.Point
    && entity.entityKind !== MateEntityKind.Origin
    && entity.entityKind !== MateEntityKind.CoordinateSystem
  ) {
    return null;
  }
  const point = Array.isArray(geometry.point)
    ? geometry.point
    : geometry.origin;
  if (!Array.isArray(point)) {
    return null;
  }
  return { point: vectorFromTuple(point as TopologyPoint3D) };
}

/**
 * 判断值是否为可序列化三维点。
 *
 * @param value 待判断值。
 * @returns 是否为三维点。
 */
function isTopologyPointTuple(value: unknown): value is TopologyPoint3D {
  return Array.isArray(value)
    && value.length === 3
    && value.every((item) => typeof item === 'number' && Number.isFinite(item));
}

/**
 * 从描述符采样点中读取第一个有效世界点。
 *
 * @param geometry BRep 几何描述符。
 * @returns 第一个有效采样点；无有效采样点时返回 null。
 */
function resolveFirstSampledPoint(
  geometry: BRepGeometryDescriptor & Record<string, unknown>,
): TopologyPoint3D | null {
  if (!Array.isArray(geometry.sampledPoints)) {
    return null;
  }
  return geometry.sampledPoints.find(isTopologyPointTuple) ?? null;
}

/**
 * 根据 descriptor 读取实体代表点。
 *
 * 设计说明：圆柱、圆锥、曲面和圆边等实体除了中心线外，通常还会携带用户选择附近的
 * 世界空间代表点。Distance Mate 默认值和预览需要优先使用这个有限点，避免无限轴线
 * 与平面相交时把有限曲面距离误判为 0。
 *
 * @param entity 实体引用。
 * @returns 代表点几何。
 */
export function resolveRepresentativePointGeometry(entity: MateEntityRef): PointGeometry | null {
  const geometry = entity.geometry as BRepGeometryDescriptor & Record<string, unknown>;
  const point = [
    geometry.point,
    geometry.start,
    geometry.end,
    geometry.center,
    geometry.origin,
    /**
     * 部分边/曲面描述符只有有限采样点。SOLIDWORKS 的 Distance Mate 默认距离
     * 与后续求解都应基于用户实际选择附近的有限几何，而不是退回无限轴线原点。
     */
    resolveFirstSampledPoint(geometry),
  ].find(isTopologyPointTuple);

  return point ? { point: vectorFromTuple(point) } : null;
}

/**
 * 根据 descriptor 读取球。
 *
 * @param entity 实体引用。
 * @returns 球几何。
 */
export function resolveSphereGeometry(entity: MateEntityRef): SphereGeometry | null {
  const geometry = entity.geometry as BRepGeometryDescriptor & Record<string, unknown>;
  if (entity.entityKind !== MateEntityKind.Sphere) {
    return null;
  }
  if (!Array.isArray(geometry.center) || typeof geometry.radius !== 'number') {
    return null;
  }
  return {
    center: vectorFromTuple(geometry.center as TopologyPoint3D),
    radius: geometry.radius,
  };
}

/**
 * 判断 transform 是否近似相等。
 *
 * @param left 左 transform。
 * @param right 右 transform。
 * @param epsilon 容差。
 * @returns 是否相等。
 */
export function areTransformsClose(
  left: ComponentTransformSnapshot,
  right: ComponentTransformSnapshot,
  epsilon: number,
): boolean {
  const isTupleClose = (a: readonly number[], b: readonly number[]): boolean => (
    a.length === b.length && a.every((value, index) => Math.abs(value - b[index]) <= epsilon)
  );
  return isTupleClose(left.position, right.position)
    && isTupleClose(left.quaternion, right.quaternion)
    && isTupleClose(left.scale, right.scale);
}
